import { i18n } from "@lingui/core";
import WarpElement from "@warp-ds/elements-core";
import "@warp-ds/elements/components/card";
import "@warp-ds/elements/components/badge";
import { css, html } from "lit";
import { trackClickEvent } from "../tracking.js";
import { TrackingEventMap, eventReference } from "../../models/trackingEvent.js";
import { EvergreenContentItem } from "../../models/evergreenContent.js";
import { activateI18n } from "../../utils/i18n.js";
import { Subvertical } from "../../constants.js";
import { getMarketingHeader } from "../../shared/evergreen-config.js";

class EvergreenContent extends WarpElement {
    declare trackingEventMap: TrackingEventMap;
    declare evergreen: EvergreenContentItem[];
    declare viewAllLink: string;
    declare subvertical: Subvertical;

    static properties = {
        trackingEventMap: { type: Object },
        evergreen: {
            attribute: true,
            type: Array,
            converter: {
                fromAttribute: (value: string) => {
                    try {
                        return value ? JSON.parse(value) : [];
                    } catch {
                        return [];
                    }
                },
            },
        },
        viewAllLink: { attribute: true, type: String },
        subvertical: { attribute: true, type: String },
    };

    static styles = [
        WarpElement.styles,
        css`
            @css-placeholder-tr4prj;

            /* No CSS needed - using inline styles for gradients */
        `,
    ];

    constructor() {
        super();
        this.evergreen = [];
    }

    connectedCallback() {
        activateI18n();
        super.connectedCallback();
    }

    private get header() {
        return getMarketingHeader(this.subvertical);
    }

    render() {
        return html`<div class="grid gap-16 min-h-[230] max-w-full isolate">
            <div class="flex items-baseline justify-between">
                <h3 class="t3 mb-0">${this.header}</h3>
                <div class="flex items-center gap-8 whitespace-nowrap">
                    <a
                        href="${this.viewAllLink}"
                        class=" text-s font-normal s-text-secondary "
                        @click="${() => trackClickEvent(this.trackingEventMap[eventReference.VIEW_ALL_EVERGREEN], this.viewAllLink)}"
                    >
                        <span class="hover:underline">${i18n.t({ id: "evergreen.view-all", message: "View all" })}</span>
                    </a>
                    <span class="s-text-primary text-m" aria-hidden="true">›</span>
                </div>
            </div>
            <div class="grid gap-16 grid-rows-3 gap-12 md:grid-cols-[632px_1fr] md:gap-16 md:grid-rows-none">
                ${this.evergreen?.slice(0, 1).map((evergreenItem: EvergreenContentItem) => {
                    return html`<a
                        href="${evergreenItem.link}"
                        aria-label="Evergreen content: ${evergreenItem.header}"
                        @click="${() => trackClickEvent(this.trackingEventMap[eventReference.EVERGREEN_CONTENT], evergreenItem.link)}"
                        class="block group"
                    >
                        <div
                            class="focus-within:outline outline-4 overflow-hidden s-outline-focused rounded-8 transition-all duration-300 hover:scale-[1.02] focus-within:scale-[1.02] h-full relative h-[240px] md:h-[356px] md:w-[632px]"
                            style="background-color: var(--w-s-color-border)"
                        >
                            <w-badge position="top-left" variant="info" class="z-20 relative whitespace-nowrap">
                                ${evergreenItem.badge}
                            </w-badge>
                            <div
                                class="absolute inset-0 w-full h-full bg-cover bg-center transition-transform duration-300 group-hover:scale-105 z-0"
                                style="background-image: url('${evergreenItem.imageUrl}');"
                            ></div>
                            <div
                                class="absolute bottom-0 left-0 right-0 z-10 pointer-events-none evg-fade-sm h-[220px]"
                                style="background: linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.58) 76%, rgba(0, 0, 0, 0.92) 100%);"
                            ></div>

                            <div class="absolute bottom-0 left-0 right-0 flex flex-col justify-end p-20 md:p-24 z-30 h-[146px]">
                                <label class="text-white md:t3 t4 leading-tight s-text-inverted">${evergreenItem.header}</label>
                            </div>
                        </div>
                    </a>`;
                })}

                <div class="grid grid-rows-2 gap-16 h-full contents md:grid">
                    ${this.evergreen.slice(1, 3).map((evergreenItem: EvergreenContentItem) => {
                        return html`<a
                            href="${evergreenItem.link}"
                            aria-label="Evergreen content: ${evergreenItem.header}"
                            @click="${() => trackClickEvent(this.trackingEventMap[eventReference.EVERGREEN_CONTENT], evergreenItem.link)}"
                            class="block group"
                        >
                            <div
                                class="focus-within:outline outline-4 overflow-hidden s-outline-focused rounded-8 transition-all duration-300 hover:scale-[1.02] focus-within:scale-[1.02] relative h-[240px] md:h-[170px] md:w-[300px]"
                                style="background-color: var(--w-s-color-border)"
                            >
                                <w-badge position="top-left" variant="info" class="z-30 relative whitespace-nowrap">
                                    ${evergreenItem.badge}
                                </w-badge>
                                <div
                                    class="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                                    style="background-image:url('${evergreenItem.imageUrl}')"
                                ></div>

                                <div
                                    class="absolute bottom-0 left-0 right-0 z-10 pointer-events-none evg-fade-sm h-[146px]"
                                    style="background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0.95) 100%);"
                                ></div>

                                <div class="absolute bottom-0 left-0 right-0 z-20 flex flex-col justify-end p-16 h-[146px]">
                                    <label class="text-white t4 leading-tight s-text-inverted"> ${evergreenItem.header} </label>
                                </div>
                            </div>
                        </a>`;
                    })}
                </div>
            </div>
        </div>`;
    }
}

customElements.define("evergreen-content", EvergreenContent as unknown as CustomElementConstructor);
